import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { fetchMessageReceipts } from '@/lib/api';
import { toast } from '@/stores/toast.store';
import type { MessageReceipts } from '@goodchat/shared/types';

interface ReceiptDetailModalProps {
  messageId: string;
  open: boolean;
  onClose: () => void;
}

export function ReceiptDetailModal({ messageId, open, onClose }: ReceiptDetailModalProps) {
  const [receipts, setReceipts] = useState<MessageReceipts | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !messageId) return;
    setLoading(true);
    fetchMessageReceipts(messageId)
      .then((data) => setReceipts(data))
      .catch(() => toast.error('Failed to load message receipts'))
      .finally(() => setLoading(false));
  }, [open, messageId]);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-700 dark:bg-gray-900">
          <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Message Details
          </Dialog.Title>

          {loading ? (
            <div className="mt-4 flex justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
            </div>
          ) : receipts ? (
            <div className="mt-4 space-y-4">
              {/* Read by */}
              <section>
                <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Read by ({receipts.reads.length})
                </h3>
                {receipts.reads.length > 0 ? (
                  <ul className="space-y-1.5">
                    {receipts.reads.map((r) => (
                      <li key={r.userId} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[10px] font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            {r.displayName.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-gray-800 dark:text-gray-200">{r.displayName}</span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(r.readAt).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-400">No reads tracked</p>
                )}
              </section>

              {/* Not read */}
              {receipts.notRead.length > 0 && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Not read ({receipts.notRead.length})
                  </h3>
                  <ul className="space-y-1.5">
                    {receipts.notRead.map((r) => (
                      <li key={r.userId} className="flex items-center gap-2 text-sm">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                          {r.displayName.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400">{r.displayName}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Acknowledgments */}
              {receipts.acknowledgments && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold text-green-700 dark:text-green-400">
                    Acknowledged ({receipts.acknowledgments.length})
                  </h3>
                  <ul className="space-y-1.5">
                    {receipts.acknowledgments.map((a) => (
                      <li key={a.userId} className="flex items-center justify-between text-sm">
                        <span className="text-gray-800 dark:text-gray-200">{a.displayName}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(a.acknowledgedAt).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {receipts.notAcknowledged && receipts.notAcknowledged.length > 0 && (
                <section>
                  <h3 className="mb-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
                    Not acknowledged ({receipts.notAcknowledged.length})
                  </h3>
                  <ul className="space-y-1.5">
                    {receipts.notAcknowledged.map((a) => (
                      <li key={a.userId} className="text-sm text-gray-600 dark:text-gray-400">
                        {a.displayName}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          ) : null}

          <Dialog.Close className="absolute right-4 top-4 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
